'use client'

import React, { useState, useEffect, useRef } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { InnocodeButton, InnocodeInput } from '@intsoft/innocode-core'
import styles from './page.module.css'
import { useScrollAnimation } from '@/hooks/useScrollAnimation'
import { TestimonialCard } from './components/TestimonialCard'
import { SolutionCard } from './components/SolutionCard'
import { motion, useMotionValue, useAnimationFrame } from 'framer-motion'

type Testimonial = {
  quoteText: string
  quoteDescription: string
  author: string
  rating: number
}

const TESTIMONIALS: Testimonial[] = [
  {
    quoteText: '"구독 현황이 한눈에 보여서 운영이 훨씬 편해졌어요."',
    quoteDescription:
      '여러 서비스의 계약 상태를 따로 정리하지 않아도 돼서 좋았습니다. 차트로 전체 현황이 정리되어 보여 기관에서 어떤 서비스가 운영 중인지, 언제 계약이 만료되는지 빠르게 파악할 수 있어요. 매번 엑셀을 열어 비교하지 않아도 되고, 현재 운영 규모를 직관적으로 확인할 수 있어 관리 방향을 잡는 데도 도움이 됩니다. 복잡했던 구독 관리가 한층 단순해졌습니다.',
    author: "'ㅎ'초 김** 선생님",
    rating: 5,
  },
  {
    quoteText: '"계정 정보를 한곳에서 관리하니 업무 시간이 줄었어요."',
    quoteDescription:
      '서비스마다 관리자 계정이 달라 확인할 때마다 메일을 찾거나 기록을 뒤져야 했는데, 에듀스에 정리해 두니 필요한 정보를 바로 확인할 수 있습니다. 담당자 변경이나 내부 점검 시에도 계정 정보를 한 화면에서 확인할 수 있어 업무 흐름이 끊기지 않아요. 단순히 정리 도구를 넘어, 관리 기준을 하나로 맞출 수 있다는 점이 가장 큰 장점이라고 느꼈습니다.',
    author: "'ㄱ'고 최** 선생님",
    rating: 4,
  },
  {
    quoteText: '"만료 일정을 놓치지 않게 되어 안심이에요."',
    quoteDescription:
      '계약 만료일을 별도로 관리하지 않아도 대시보드에서 바로 확인할 수 있어요. 이전에는 달력이나 엑셀에 따로 정리해두지 않으면 놓치는 경우가 있었는데, 이제는 중앙 화면에서 일정과 상태를 함께 볼 수 있어 훨씬 안정적으로 운영할 수 있습니다. 사전에 준비할 수 있다는 점만으로도 업무 부담이 크게 줄었습니다.',
    author: "'ㅇ'중 박** 선생님",
    rating: 5,
  },
  {
    quoteText: '"사용 현황이 정리되어 비용 점검이 쉬워졌어요."',
    quoteDescription:
      '어떤 서비스가 얼마나 활용되고 있는지 파악하기 어려워 막연히 유지하던 경우가 많았습니다. 에듀스를 사용하면서 전체 사용 현황을 정리해 볼 수 있어 실제 운영에 필요한 서비스가 무엇인지 판단하기 쉬워졌어요. 불필요하게 유지되던 구독을 점검할 수 있게 되면서 예산 관리도 훨씬 체계적으로 바뀌었습니다.',
    author: "'ㅁ'고 정** 선생님",
    rating: 5,
  },
  {
    quoteText: '"라이선스 수를 체계적으로 기록할 수 있어 좋아요."',
    quoteDescription:
      '운영 중인 라이선스 수를 따로 관리하지 않으면 시간이 지날수록 혼선이 생기기 쉬웠습니다. 에듀스에서는 서비스별 라이선스 수와 담당자를 함께 정리해둘 수 있어 현재 운영 규모를 명확히 파악할 수 있어요. 내부 점검이나 보고가 필요할 때도 자료를 다시 만들 필요 없이 바로 확인할 수 있어 관리가 훨씬 체계적으로 느껴집니다.',
    author: "'ㅅ'중 정** 선생님",
    rating: 5,
  },
  {
    quoteText: '"흩어진 계약 정보를 한 화면에서 확인할 수 있어 편리해요."',
    quoteDescription:
      '여러 문서를 번갈아 보지 않아도 중앙 화면에서 계약 정보를 확인할 수 있어 점검 시간이 크게 줄었습니다. 계약 기간, 상태, 관련 정보를 한곳에 정리해두니 관리 기준이 명확해졌고, 반복적인 확인 작업도 줄어들었어요. 단순한 정리를 넘어 운영 전반을 체계화하는 데 도움이 되고 있습니다.',
    author: "'ㄷ'고 윤** 선생님",
    rating: 5,
  },

  // {
  //   quoteText: '"관리업무가 줄어들면서<br/>업무시간이 70% 줄어들었어요."',
  //   quoteDescription:
  //     '팀에서 사용하고 있는 서비스는 많은데 여기저기 흩어져서 보기 불편하더라고요. 그래서 찾아보다 에듀스를 알게 됐는데 한곳에 모아서 관리할 수 있어 편해졌어요. 덕분에 복잡하게 찾아보고 결제하고 할 거 없이 문의 한번으로 알아서 서비스가 등록되니 다른 일에 더 집중할 수 있어 편했습니다.',
  //   author: "'ㅎ'초 김** 선생님",
  //   rating: 5,
  // },
  // {
  //   quoteText: '"팀원별 툴 이용방식에 따라 접근 상태를 관리할 수 있어서 비용 절감에 도움돼요."',
  //   quoteDescription:
  //     '사람마다 툴을 쓰는 방식이 다르다 보니, 누군가는 A툴이, 다른 누군가는 B툴이 더 잘 맞는 현상이 있어 일괄적으로 비싼 플랜을 사용하는 게 좀 불편했었는데 에듀스에는 사용자마다 다르게 접근권한을 부여할 수 있어서 개별 관리가 가능해 모두를 만족시킬 수 있어 좋았어요.',
  //   author: "'ㅇ'중 박** 선생님",
  //   rating: 4,
  // },
  // {
  //   quoteText: '"타 서비스보다 <br/>결제 시스템이 안정적이라 안심돼요."',
  //   quoteDescription:
  //     "이전에는 다른 서비스를 이용할 때 안심하고 이용할 수 있는 곳이 맞는지 걱정됐는데 에듀스에 문의해보니 'ISMS'라는 정보보호 관리 인증이 있어 걱정하지 않아도 돼서 좋아요.",
  //   author: "'ㅁ'고 정** 선생님",
  //   rating: 5,
  // }, 
  // {
  //   quoteText: '"구독 만료일을 한눈에 볼 수 있어서 좋아요."',
  //   quoteDescription:
  //     '이전에는 서비스마다 만료일이 달라서 엑셀로 정리하고 따로 알림을 설정해야 했어요. 한 번 놓치면 수업 시 사용이 불가능해져서 수업때 당황한 적도 있었고요. 에듀스를 사용한 이후에는 만료 일정이 한눈에 보이고 사전 알림까지 받아서 불필요한 지출을 막을 수 있게 됐습니다.',
  //   author: "'ㅇ'중 박** 선생님",
  //   rating: 5,
  // },
  // {
  //   quoteText: '"퇴사·이동 인력 관리가 훨씬 수월해졌어요."',
  //   quoteDescription:
  //     '담당자가 바뀌거나 퇴사할 때마다 이메일을 받아서 다시 설정해야하는 계정 정리가 번거로웠는데, 에듀스에서는 사용자별 접근 권한을 쉽게 변경할 수 있어 관리가 간편해졌습니다. 보안 걱정도 줄어들었고요.',
  //   author: "'ㅇ'중 박** 행정실장",
  //   rating: 4,
  // },
  // {
  //   quoteText: '"사용 현황을 한눈에 볼 수 있어서 관리가 쉬워졌어요."',
  //   quoteDescription:
  //     '누가 어떤 서비스를 얼마나 사용하고 있는지 파악하기 어려워서 계속 개별적으로 확인해야 했어요. 에듀스 도입 이후에는 사용 현황이 정리되어 보여서 불필요한 서비스는 정리하고, 꼭 필요한 서비스에는 더 집중할 수 있게 됐습니다.',
  //   author: "'ㅁ'고 정** 선생님",
  //   rating: 5,
  // },
  // {
  //   quoteText: '"결제 관리가 단순해져서 실수가 줄었어요."',
  //   quoteDescription:
  //     '여러 카드와 계좌로 나눠 결제하다 보니 정산할 때마다 확인해야 할 게 많았어요. 작은 실수가 반복되면서 행정 부담이 커졌죠. 에듀스를 사용한 이후에는 결제 흐름이 정리되어 보여서 확인 과정이 단순해졌고, 실수도 눈에 띄게 줄었습니다.',
  //   author: "'ㄱ'중 윤** 선생님",
  //   rating: 5,
  // },
]

export default function LandingPage() {
  const router = useRouter()
  const [isMobileWindow, setIsMobileWindow] = useState(false)
  const [email, setEmail] = useState('')

  // 스크롤 애니메이션 훅들 - 각 요소마다 별도로 생성해야 합니다
  const heroTitleAnimation = useScrollAnimation('animate__fadeInUp', { threshold: 0.2, triggerOnce: true, enterAnimation: 'animate__fadeInUp' })
  const heroDescriptionAnimation = useScrollAnimation('animate__fadeInUp', { threshold: 0.2, triggerOnce: true, enterAnimation: 'animate__fadeInUp' })
  const featureTitleAnimation = useScrollAnimation('animate__fadeInUp', { threshold: 0.2, triggerOnce: true, enterAnimation: 'animate__fadeInUp' })
  const featureCardsAnimation = useScrollAnimation('animate__fadeInUp', { threshold: 0.2, triggerOnce: true, enterAnimation: 'animate__fadeInUp' })
  const licenseTextAnimation = useScrollAnimation('animate__fadeInUp', { threshold: 0.2, triggerOnce: true, enterAnimation: 'animate__fadeInUp' })
  const licenseImageAnimation = useScrollAnimation('animate__fadeInUp', { threshold: 0.2, triggerOnce: true, enterAnimation: 'animate__fadeInUp' })
  const subscriptionTextAnimation = useScrollAnimation('animate__fadeInUp', { threshold: 0.2, triggerOnce: true, enterAnimation: 'animate__fadeInUp' })
  const subscriptionImageAnimation = useScrollAnimation('animate__fadeInUp', { threshold: 0.2, triggerOnce: true, enterAnimation: 'animate__fadeInUp' })
  const testimonialHeaderAnimation = useScrollAnimation('animate__fadeInUp', { threshold: 0.2, triggerOnce: true, enterAnimation: 'animate__fadeInUp' })
  const solutionHeaderAnimation = useScrollAnimation('animate__fadeInUp', { threshold: 0.2, triggerOnce: true, enterAnimation: 'animate__fadeInUp' })
  const solutionCardsAnimation = useScrollAnimation('animate__fadeInUp', { threshold: 0.2, triggerOnce: true, enterAnimation: 'animate__fadeInUp' })

  const testimonialTrackRef = useRef<HTMLDivElement | null>(null)
  const testimonialX = useMotionValue(0)

  useAnimationFrame((t, delta) => {
    const speed = 100 // px per second
    const moveBy = (speed * delta) / 1000

    let currentX = testimonialX.get() - moveBy

    const track = testimonialTrackRef.current
    if (track) {
      const resetPoint = -track.scrollWidth / 2
      if (currentX <= resetPoint) {
        currentX += track.scrollWidth / 2;
      }
    }

    testimonialX.set(currentX)
  })

  useEffect(() => {
    const handleResize = () => setIsMobileWindow(window.innerWidth < 768)
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleInquiry = () => router.push('/contact')
  const handleProduct = () => router.push('/products')

  return (
    <div className={styles.container}>
      <img className={styles.heroImage} src="/assets/images/landing1.png" alt="Hero" />
      <div className={styles.mainContent}>
        <div className={styles.heroSection + ' ' + styles.section}>
          <div className={styles.heroContent}>
            <div ref={heroTitleAnimation.ref} className={`${styles.heroTitle} ${heroTitleAnimation.className}`} style={{ visibility: heroTitleAnimation.inViewport ? 'visible' : 'hidden' }}>
              똑똑하게 <br />효율적으로
            </div>
            <div ref={heroDescriptionAnimation.ref} className={`${styles.heroDescription} ${heroDescriptionAnimation.className}`} style={{ visibility: heroDescriptionAnimation.inViewport ? 'visible' : 'hidden' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div className={styles.descriptionText}>
                  복잡한 구독 관리를 단순하게 바꿉니다.
                </div>
                <div className={styles.descriptionText}>
                  흩어져 있던 구독 서비스를 한곳에 모읍니다. 모든 구독 현황을 차트로 직관적으로 확인하고, 계정 관리, 사용 상태 관리까지 운영에 필요한 모든 과정을 하나의 관리도구로 통합합니다. 교육 환경의 운영을 고려한 설계로 반복적인 행정 업무와 관리 부담을 줄이고, 운영 현황을 한눈에 파악할 수 있습니다. 관리 효율을 높이고 일원화된 관리 시스템을 구축할 수 있도록 지원합니다.
                </div>
                <div className={styles.descriptionText}>
                  공공기관부터 기업까지, adduce의 구독 관리 솔루션으로 업무 효율을 극대화하세요.
                </div>
              </div>
              <div className={styles.buttonGroup}>
                <InnocodeButton
                  size="md"
                  designType="primary"
                  theme="line"
                  onClick={handleInquiry}
                >
                  도입문의
                </InnocodeButton>
                <InnocodeButton
                  size="md"
                  designType="primary"
                  theme="line"
                  onClick={handleProduct}
                >
                  상품 소개
                </InnocodeButton>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.sectionSpacer}></div>

        {/* Feature Section */}
        <div className={styles.featureSection + " " + styles.section}>
          <div ref={featureTitleAnimation.ref} className={`${styles.featureTitle} ${featureTitleAnimation.className}`} style={{ visibility: featureTitleAnimation.inViewport ? 'visible' : 'hidden' }}>
            가장 쉬운 구독 관리의 시작,<br />에듀스와 함께하세요.
          </div>
          <div ref={featureCardsAnimation.ref} className={`${styles.featureCards} ${featureCardsAnimation.className}`} style={{ visibility: featureCardsAnimation.inViewport ? 'visible' : 'hidden' }}>
            <div className={`${styles.featureCard}`}>
              <img className={styles.cardIcon} src="/assets/icon/sign.jpg" alt="Feature 1" />
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>계약 현황은 한눈에</div>
                <div className={styles.cardDescription}>
                  효율적인 행정 모든 구독 상품의 계약 기간과 상태를 차트 형식으로 직관적이고 빠르게 파악할 수 있어요.
                </div>
              </div>
            </div>
            <div className={`${styles.featureCard}`}>
              <img className={styles.cardIcon} src="/assets/icon/accountInfo.jpg" alt="Feature 2" />
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>구독 • 계정 통합관리</div>
                <div className={styles.cardDescription}>
                  계정 정보와 선택한 요금제를 한 곳에 모아 보관하여 번거로운 메일 검색 없이 즉시 필요한 정보를 확인해 보세요.
                </div>
              </div>
            </div>
            <div className={`${styles.featureCard}`}>
              <img className={styles.cardIcon} src="/assets/icon/license.jpg" alt="Feature 3" />
              <div className={styles.cardContent}>
                <div className={styles.cardTitle}>라이선스 관리</div>
                <div className={styles.cardDescription}>
                  라이선스별 실제 이용자를 지정해 사용 현황을 효율적으로 관리하고 내부 보고를 간편하게 처리해 보세요.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.sectionSpacer}></div>

        {/* Image + Text Section 1 */}
        <div className={styles.licenseSection + ' ' + styles.section}>
          <div ref={licenseTextAnimation.ref} className={`${styles.textContent} ${licenseTextAnimation.className}`} style={{ visibility: licenseTextAnimation.inViewport ? 'visible' : 'hidden' }}>
            <div className={styles.sectionTitle}>
              라이센스 관리 도구로 <br />계정별 사용자 접근 <br />상태를 관리할 수 있어요.
            </div>
            <div className={styles.sectionDescription}>
              계정 정보를 한곳에 모아 운영 리스크를 줄이고,<br />사용자별 접근 상태를 체계적으로 관리해 보세요.
            </div>
          </div>
          <div ref={licenseImageAnimation.ref} className={`${styles.licenseSectionImageContainer} ${licenseImageAnimation.className}`} style={{ visibility: licenseImageAnimation.inViewport ? 'visible' : 'hidden' }}>
            <img className={styles.licenseSectionImage} src="/assets/images/landing2.png" alt="License Management" />
          </div>
        </div>

        <div className={styles.sectionSpacer}></div>

        {/* Image + Text Section 2 (구독 서비스) */}
        <div className={`${styles.subscriptionSection} ${styles.section}`}>
          <div ref={subscriptionImageAnimation.ref} className={`${styles.subscriptionSectionImageContainer} ${subscriptionImageAnimation.className}`} style={{ visibility: subscriptionImageAnimation.inViewport ? 'visible' : 'hidden' }}>
            <img className={styles.subscriptionSectionImage} src="/assets/images/landing3.png" alt="Subscription Management" />
          </div>
          <div ref={subscriptionTextAnimation.ref} className={`${styles.textContent} ${subscriptionTextAnimation.className}`} style={{ visibility: subscriptionTextAnimation.inViewport ? 'visible' : 'hidden' }}>
            <div className={styles.sectionTitle}>
              구독중인 서비스들을<br />한눈에 모아 보세요.
            </div>
            <div className={styles.sectionDescription}>
              전용 관리 툴을 통해 기관 내 구독 현황을 실시간으로 확인해 보세요. 직관적인 구독 관리 시스템으로 쉽고 효율적인 운영이 가능해집니다.
            </div>
          </div>
        </div>

        <div className={styles.sectionSpacer}></div>

        {/* Testimonial Section */}
        <div className={styles.testimonialSection + ' ' + styles.section}>
          <div ref={testimonialHeaderAnimation.ref} className={`${styles.testimonialHeader} ${testimonialHeaderAnimation.className}`} style={{ visibility: testimonialHeaderAnimation.inViewport ? 'visible' : 'hidden' }}>
            <div className={styles.testimonialTitle}>
              관리 도구를 통해<br />시간과 비용을 절감하세요.
            </div>
            <div className={styles.testimonialSubtitle}>
              구독 관리는 에듀스에 맡기고 더 중요한 일에 몰입하세요.
            </div>
          </div>
          <div className={styles.testimonialCards}>

            <motion.div
              ref={testimonialTrackRef}
              className={styles.testimonialCardsTrack}
              style={{ x: testimonialX }}
            >
              {[...TESTIMONIALS, ...TESTIMONIALS].map((item, index) => (
                <TestimonialCard
                  key={index}
                  styles={styles as Record<string, string>}
                  quoteText={item.quoteText}
                  quoteDescription={item.quoteDescription}
                  author={item.author}
                  starCount={item.rating}
                />
              ))}
            </motion.div>
          </div>
        </div>

        <div className={styles.sectionSpacer}></div>

        {/* Solution Section */}
        <div className={styles.solutionSection + ' ' + styles.section}>
          <div ref={solutionHeaderAnimation.ref} className={`${styles.solutionHeader} ${solutionHeaderAnimation.className}`} style={{ visibility: solutionHeaderAnimation.inViewport ? 'visible' : 'hidden' }}>
            <div className={styles.solutionTitle}>
              복잡한 구독관리,<br />에듀스 하나로 완벽하게
            </div>
            <div className={styles.solutionSubtitle}>
              안정적인 관리 도구로 업무시간부터 비용까지<br />구독 서비스 관리는 에듀스에 맡기고 중요한 일에 <br />집중할 수 있도록 도와드릴게요.
            </div>
          </div>
          <div ref={solutionCardsAnimation.ref} className={`${styles.solutionCards} ${solutionCardsAnimation.className}`} style={{ visibility: solutionCardsAnimation.inViewport ? 'visible' : 'hidden' }}>
            {[
              {
                title: '구독 관리 시스템',
                description: '흩어진 구독 결제 내역과 계약 현황을 <br/>중앙 대시보드에서 통합 관리합니다.',
                icon: <svg className={styles.solutionCardIconInner} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9.586 2.586A2 2 0 0 1 11 2h2a2 2 0 0 1 2 2v.089l.473.196.063-.063a2.002 2.002 0 0 1 2.828 0l1.414 1.414a2 2 0 0 1 0 2.827l-.063.064.196.473H20a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-.089l-.196.473.063.063a2.002 2.002 0 0 1 0 2.828l-1.414 1.414a2 2 0 0 1-2.828 0l-.063-.063-.473.196V20a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2v-.089l-.473-.196-.063.063a2.002 2.002 0 0 1-2.828 0l-1.414-1.414a2 2 0 0 1 0-2.827l.063-.064L4.089 15H4a2 2 0 0 1-2-2v-2a2 2 0 0 1 2-2h.09l.195-.473-.063-.063a2 2 0 0 1 0-2.828l1.414-1.414a2 2 0 0 1 2.827 0l.064.063L9 4.089V4a2 2 0 0 1 .586-1.414ZM8 12a4 4 0 1 1 8 0 4 4 0 0 1-8 0Z" />
                </svg>
                ,
              },
              {
                title: '다양한 솔루션',
                description: '흩어진 구독 결제 내역과 계약 현황을 <br/>중앙 대시보드에서 통합 관리합니다.',
                icon: <svg className={styles.solutionCardIconInner} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.644 3.066a1 1 0 0 1 .712 0l7 2.666A1 1 0 0 1 20 6.68a17.694 17.694 0 0 1-2.023 7.98 17.406 17.406 0 0 1-5.402 6.158 1 1 0 0 1-1.15 0 17.405 17.405 0 0 1-5.403-6.157A17.695 17.695 0 0 1 4 6.68a1 1 0 0 1 .644-.949l7-2.666Zm4.014 7.187a1 1 0 0 0-1.316-1.506l-3.296 2.884-.839-.838a1 1 0 0 0-1.414 1.414l1.5 1.5a1 1 0 0 0 1.366.046l4-3.5Z" />
                </svg>
                ,
              },
              {
                title: '불필요한 지출 최적화',
                description: '흩어진 구독 결제 내역과 계약 현황을 <br/>중앙 대시보드에서 통합 관리합니다.',
                icon: <svg className={styles.solutionCardIconInner} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 14a3 3 0 0 1 3-3h4a2 2 0 0 1 2 2v2a2 2 0 0 1-2 2h-4a3 3 0 0 1-3-3Zm3-1a1 1 0 1 0 0 2h4v-2h-4Z" />
                  <path d="M12.293 3.293a1 1 0 0 1 1.414 0L16.414 6h-2.828l-1.293-1.293a1 1 0 0 1 0-1.414ZM12.414 6 9.707 3.293a1 1 0 0 0-1.414 0L5.586 6h6.828ZM4.586 7l-.056.055A2 2 0 0 0 3 9v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2h-4a5 5 0 0 1 0-10h4a2 2 0 0 0-1.53-1.945L17.414 7H4.586Z" />
                </svg>
                ,
              },
              {
                title: '편리한 통합 결제',
                description: '흩어진 구독 결제 내역과 계약 현황을 <br/>중앙 대시보드에서 통합 관리합니다.',
                icon: <svg className={styles.solutionCardIconInner} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M4 5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2H4Zm0 6h16v6H4v-6Z" />
                  <path d="M5 14a1 1 0 0 1 1-1h2a1 1 0 1 1 0 2H6a1 1 0 0 1-1-1Zm5 0a1 1 0 0 1 1-1h5a1 1 0 1 1 0 2h-5a1 1 0 0 1-1-1Z" />
                </svg>
                ,
              },
            ].map((item, index) => (
              <SolutionCard
                key={index}
                title={item.title}
                description={item.description}
                icon={item.icon}
                styles={styles as Record<string, string>}
              />
            ))}
          </div>
        </div>

        <div className={styles.sectionSpacer}></div>
      </div>

      {/* CTA Section */}
      <div className={styles.ctaSection}>
        <div className={styles.ctaContent}>
          <div className={styles.ctaSubtitle}>막막한 구독관리</div>
          <div className={styles.ctaTitle}>지금 adduce로 시작하세요.</div>
          <div className={styles.ctaDescription}>
            전문 컨설턴트가 여러분을 기다리고 있어요.<br />구매 전, 여러분의 상황에 맞는 최적의 솔루션을 발견하세요.
          </div>
        </div>
        <div className={styles.ctaForm}>
          <InnocodeButton
            size="lg"
            designType="primary"
            onClick={handleInquiry}
            backgroundColor="white"
            textColor=" var(--primary-filled-normal, #297aff)"
            className={styles.ctaButton}
          >
            상담 문의
          </InnocodeButton>
        </div>
      </div>
    </div>
  )
}
